import { AxiosRequestConfig } from 'axios';

import { getAccessTokenRequest } from 'store/api/actions';
import { store } from 'store/configureStore';
import { openModal as openModalAction } from 'store/modals/actions';

import { ClientError, FailedRequestType, onRejectedResponseProps } from 'services/api/ApiClientService/types';

import { ModalName } from 'modals/types';

// Intercepts and handles a rejected response.
const onRejectedResponse =
    ({
        errorHandler,
        onAuthorisationError,
        serverErrorUrls,
        isServerErrorModalOpened,
        isTokenRefreshInProgress,
        failedRequestsRequiredToken,
        instance,
        headerTokenKey,
    }: onRejectedResponseProps) =>
    (error: ClientError) => {
        const status = error?.response?.status;
        const requestRequiresAccessToken = Boolean(
            error.config?.headers && (error.config.headers as any)['auth-token']
        );

        if (status && status >= 500 && status < 600) {
            const url = error?.config?.url;
            const retryMeta = error?.config?.['axios-retry'];
            const retryCount = retryMeta?.retryCount ?? 0;
            const retriesConfigured = retryMeta?.retries ?? 0;

            if (retryCount >= retriesConfigured) {
                if (url) serverErrorUrls.add(url);

                if (!isServerErrorModalOpened.current) {
                    store.dispatch(openModalAction(ModalName.ServerErrorModal));
                    isServerErrorModalOpened.current = true;
                }
            }
        }

        if (error.response && requestRequiresAccessToken && error.response.status === 401) {
            if (!isTokenRefreshInProgress.current) {
                isTokenRefreshInProgress.current = true;

                store.dispatch(
                    getAccessTokenRequest({
                        onError: () => {
                            failedRequestsRequiredToken.current.forEach((req: FailedRequestType) => req.reject(error));
                        },
                        onSuccess: (token: string) => {
                            failedRequestsRequiredToken.current.forEach((req: FailedRequestType) => {
                                if (req.config.headers) {
                                    req.config.headers[headerTokenKey] = token as any;
                                } else {
                                    req.config.headers = { [headerTokenKey]: token } as any;
                                }
                                req.resolve(instance.request(req.config));
                            });
                        },
                        onFinally: () => {
                            failedRequestsRequiredToken.current = [];
                            isTokenRefreshInProgress.current = false;
                        },
                    })
                );
            }

            return new Promise((resolve, reject) => {
                failedRequestsRequiredToken.current = [
                    ...failedRequestsRequiredToken.current,
                    {
                        config: error.config as AxiosRequestConfig,
                        resolve,
                        reject,
                    },
                ];
            });
        }

        if (onAuthorisationError) {
            const status = error?.response?.status;

            if (status === 403 || status === 401) {
                onAuthorisationError(error);
            }
        }

        errorHandler(error);

        return Promise.reject(error);
    };

export default onRejectedResponse;
